/**
 * What kind of thing a security symbol names.
 *
 * A deep-dive is reached by symbol, and the kinds have different master-record
 * queries — `getMetaMutualFund` for a scheme, `getPortfolio` for a model. The
 * kind is never derived from the symbol; it is carried from whichever API
 * produced the row. The catalogue comes out of `getAllMutualFunds`, so those
 * are funds; a portfolio flagged `isModel` is a model. Each list hook tags its
 * rows as it builds them and the deep-dive reads the tag.
 */
export const SECURITY_KIND = {
  MF: 'mf',
  MODEL: 'model',
};

/** Singular labels, for headers and empty states. */
export const SECURITY_KIND_LABEL = {
  [SECURITY_KIND.MF]: 'Fund',
  [SECURITY_KIND.MODEL]: 'Model portfolio',
};

/** What the master record is called, for "no … for X" messages. */
export const SECURITY_KIND_RECORD = {
  [SECURITY_KIND.MF]: 'scheme master record',
  [SECURITY_KIND.MODEL]: 'model portfolio',
};

/**
 * What a symbol IS, in the register's own words — the backend's
 * `SecurityTypeEnum`, which every watchlist write is validated against before
 * its resolver runs (`Invalid security type: …` otherwise).
 *
 * A scheme is `mf` here. Never `mutual_fund`: that is what the app used to
 * send and what the prompts still say, and it failed every fund add and
 * remove — from the ☆ buttons and the agent alike — while `has()` could never
 * match a row the server had stored as `mf`.
 *
 * Lives in lib/ rather than in the watchlist hook because two layers need the
 * same vocabulary and neither may import the other: the data hooks put it on
 * the wire, and the `agent` slice validates what the agent asked for.
 */
export const WATCH_TYPES = {
  fund: 'mf',
  etf: 'etf',
  stock: 'stock',
  portfolio: 'portfolio',
};

const WATCH_TYPE_VALUES = Object.values(WATCH_TYPES);

/**
 * Other spellings of the same type, keyed as `normOption` normalises them —
 * lower-case with spaces, underscores and hyphens removed, so `Mutual Fund`,
 * `mutual_fund` and `MUTUAL-FUND` all land on the same entry.
 *
 * These are the words the prompts, the catalogue and older saved links use.
 */
const WATCH_TYPE_ALIASES = {
  mutualfund: WATCH_TYPES.fund,
  fund: WATCH_TYPES.fund,
  scheme: WATCH_TYPES.fund,
  model: WATCH_TYPES.portfolio,
  modelportfolio: WATCH_TYPES.portfolio,
};

const normType = (raw) => String(raw ?? '').toLowerCase().replace(/[\s_-]/g, '');

/**
 * A `securityType` in the register's vocabulary, whatever spelling arrived.
 * Blank means a scheme, which is what every caller defaults to. A word with no
 * canonical form is returned normalised rather than guessed at, so the caller
 * can decide whether to drop it (the agent slice) or let the server refuse it.
 */
export const watchType = (raw) => {
  const s = normType(raw);
  if (!s) return WATCH_TYPES.fund;
  return WATCH_TYPE_ALIASES[s] ?? s;
};

/** The same, but only when the register actually has that type; else undefined. */
export const knownWatchType = (raw) => {
  const t = watchType(raw);
  return WATCH_TYPE_VALUES.includes(t) ? t : undefined;
};
